/**
 * search-discovery.js
 * Experiment: ACC_CF_Search_09 (CommaFootball) — Broadcast 3.7.1
 *
 * FEATURE
 *   Focusing the search input opens the predictive-search results panel and
 *   shows the discovery blocks (Most searched chips + Bestsellers) while the
 *   field is empty. Discovery hides the moment the field has a value OR a chip
 *   is clicked, handing the panel over to Broadcast's live predictive results.
 *   Clearing the field reverts to discovery. The × button / Escape / outside
 *   click closes it.
 *
 * STATE CLASSES (all readable from CSS)
 *   #search-popdown.is-visible   — popdown open (theme)
 *   #search-popdown.is-searched  — results panel revealed (theme + focus handler)
 *   predictive-search.is-active  — search open/focused (this file)
 *   predictive-search.is-typing  — the field currently has a value (this file)
 *
 * WHY A SEPARATE ASSET (not the snippet's {% javascript %})
 *   ext-search-input-layout is rendered twice (desktop + mobile) and Broadcast
 *   may inject the popdown markup via the Section Rendering API. A standalone
 *   asset loaded once in theme.liquid always runs, on every page, regardless of
 *   how/when the popdown mounts.
 *
 * HOW IT COOPERATES WITH THE THEME
 *   Broadcast reveals .search-popdown__results by adding `is-searched` to
 *   [data-search-popdown] (#search-popdown). We reuse that exact class so the
 *   panel opens with the theme's own animation. The theme's reset() removes
 *   `is-searched` at <=1 char — a MutationObserver re-asserts it while the field
 *   is focused & empty, so clearing reverts to discovery instead of closing.
 *
 * THEME EDITOR (design mode)
 *   Editing the header re-renders the section and injects fresh markup. We
 *   re-init on `shopify:section:load`/`:select` and tear down the old controller
 *   on `shopify:section:unload` so document-level listeners don't accumulate.
 *   These events never fire on the live storefront — production is unaffected.
 */
(function () {
  'use strict';

  var SELECTORS = {
    predictiveSearch: 'predictive-search',
    popdown: '[data-search-popdown]', // #search-popdown — the element the theme flags
    input: '#SearchInput, [data-predictive-search-input]',
    discovery: '[data-search-discovery]',
    chip: '[data-search-discovery-chip]',
    close: '[data-search-discovery-close]',
  };

  var CLASSES = {
    // The theme's OWN reveal class. Reused, not invented — guarantees the native
    // open/close transition and avoids a second, mismatched animation.
    searched: 'is-searched',
    // From the snippet's stylesheet: display:none on the discovery wrapper.
    typing: 'search-discovery--typing',
    // On the <predictive-search> root while the field has a value. Lets CSS
    // restyle the whole component (header + panel + discovery) mid-search.
    isTyping: 'is-typing',
    // On the <predictive-search> root while the search is open/focused,
    // regardless of whether the field has a value yet.
    isActive: 'is-active',
  };

  function hasValue(input) {
    return input.value.trim().length > 0;
  }

  /**
   * One controller per <predictive-search> instance, so the desktop and mobile
   * search fields manage their own panels and never toggle each other. This
   * mirrors Broadcast's own class-per-instance pattern.
   */
  function SearchDiscovery(root) {
    this.root = root;
    this.popdown = root.querySelector(SELECTORS.popdown);
    this.input = root.querySelector(SELECTORS.input);
    this.discoveryBlocks = root.querySelectorAll(SELECTORS.discovery);

    // No popdown/input, or discovery disabled in the editor → leave the theme
    // entirely native. The feature simply no-ops.
    if (!this.popdown || !this.input || !this.discoveryBlocks.length) return;

    // Flag so the MutationObserver knows a *legitimate* close is happening
    // (× / Escape / outside-click) and shouldn't re-open the panel.
    this.allowClose = false;

    // Pre-bind the handlers that need a stable reference for removeEventListener
    // (anything attached to `document`). Inline .bind(this) creates a new
    // function each call, which can never be removed — a classic listener leak.
    this.boundDocumentClick = this.onDocumentClick.bind(this);

    this.bind();
  }

  SearchDiscovery.prototype.bind = function () {
    // 1) Focus opens the panel + shows discovery. `focus` (not `click`) so
    //    keyboard tabbing into the field works too.
    this.input.addEventListener('focus', this.open.bind(this));

    // 2) Typing toggles discovery vs. results. Runs with NO debounce, while the
    //    theme's own input handler is debounced 300ms — so discovery disappears
    //    the instant a character lands, ahead of the results fetch.
    this.input.addEventListener('input', this.syncDiscovery.bind(this));

    // 3) Chip click — delegated on root so it survives any re-render of the
    //    chips. Fills the input and runs the theme's predictive search.
    this.root.addEventListener('click', this.onChipClick.bind(this));

    // 3b) Close (×) button — clears the field and closes the panel.
    this.root.addEventListener('click', this.onCloseClick.bind(this));

    // 4) The cooperation piece. Watch `is-searched` on the popdown; if the theme
    //    strips it while the field is focused & empty (its reset() at <=1 char),
    //    re-assert it so we revert to discovery rather than collapsing.
    //    MutationObserver reacts as a microtask right after the mutation — no
    //    visible flicker, and decoupled from the theme's 300ms debounce constant.
    this.observer = new MutationObserver(this.onPopdownClassChange.bind(this));
    this.observer.observe(this.popdown, { attributes: true, attributeFilter: ['class'] });

    // 5) Close on Escape or a click outside this instance — but only when the
    //    field is empty. If there are live results, leave the theme in control.
    this.input.addEventListener('keyup', this.onKeyup.bind(this));
    document.addEventListener('click', this.boundDocumentClick);
  };

  // Tear-down for the theme editor: remove the only listener/observer that don't
  // get garbage-collected with the discarded section markup. The on-node
  // listeners (focus/input/keyup/root click) die with their detached nodes.
  SearchDiscovery.prototype.disconnect = function () {
    document.removeEventListener('click', this.boundDocumentClick);
    if (this.observer) this.observer.disconnect();
  };

  SearchDiscovery.prototype.open = function () {
    this.allowClose = false;
    this.root.classList.add(CLASSES.isActive);
    this.popdown.classList.add(CLASSES.searched);
    this.syncDiscovery();
  };

  SearchDiscovery.prototype.close = function () {
    this.allowClose = true; // tell the observer this removal is intentional
    this.root.classList.remove(CLASSES.isActive, CLASSES.isTyping);
    this.popdown.classList.remove(CLASSES.searched);
  };

  // Discovery shows only on an empty field; it hides the instant the user types.
  SearchDiscovery.prototype.syncDiscovery = function () {
    var typing = hasValue(this.input);

    this.discoveryBlocks.forEach(function (block) {
      block.classList.toggle(CLASSES.typing, typing);
    });

    // Mark the component root as being typed in, so CSS can restyle the whole
    // search component (header + panel + discovery) from a single class.
    this.root.classList.toggle(CLASSES.isTyping, typing);

    // While focused & empty, keep the panel open in its discovery state.
    if (!typing && document.activeElement === this.input) {
      this.popdown.classList.add(CLASSES.searched);
    }
  };

  SearchDiscovery.prototype.onChipClick = function (event) {
    var chip = event.target.closest(SELECTORS.chip);
    if (!chip) return;
    event.preventDefault();

    this.input.value = chip.getAttribute('data-keyword') || '';
    this.input.focus();
    this.popdown.classList.add(CLASSES.searched);

    // Broadcast's predictive search debounces on `input`. One bubbling `input`
    // event is exactly its trigger — no synthetic keypress / form submit needed.
    this.input.dispatchEvent(new Event('input', { bubbles: true }));

    this.syncDiscovery(); // field now has a value → discovery hides
  };

  SearchDiscovery.prototype.onCloseClick = function (event) {
    var closeBtn = event.target.closest(SELECTORS.close);
    if (!closeBtn) return;
    event.preventDefault();

    // Empty the field and let the theme tear down its own results (the bubbling
    // 'input' triggers Broadcast's reset at <=1 char).
    this.input.value = '';
    this.input.dispatchEvent(new Event('input', { bubbles: true }));

    // close() sets allowClose so the observer won't re-open; blur() drops focus
    // so the focusedEmpty re-assert check also fails — the panel stays shut.
    this.close();
    this.input.blur();
  };

  SearchDiscovery.prototype.onPopdownClassChange = function () {
    if (this.allowClose) return; // a real close is in progress; don't fight it

    var open = this.popdown.classList.contains(CLASSES.searched);
    var focusedEmpty = document.activeElement === this.input && !hasValue(this.input);

    // Theme stripped `is-searched` but the user is still in an empty field →
    // re-assert it to revert to discovery. (Re-adding fires this observer again,
    // but then `open` is true, so it no-ops — no infinite loop.)
    if (!open && focusedEmpty) {
      this.popdown.classList.add(CLASSES.searched);
    }
  };

  SearchDiscovery.prototype.onKeyup = function (event) {
    if (event.key === 'Escape') this.close();
  };

  SearchDiscovery.prototype.onDocumentClick = function (event) {
    if (this.root.contains(event.target)) return; // click inside this instance
    if (hasValue(this.input)) return; // live results showing → theme owns the close
    this.close();
  };

  // --- init / re-init ---------------------------------------------------------

  // `scope` may be: undefined (whole document), a section container that holds
  // predictive-search elements, or a predictive-search element itself.
  function init(scope) {
    var root = scope || document;
    var nodes;

    if (root.matches && root.matches(SELECTORS.predictiveSearch)) {
      nodes = [root];
    } else {
      nodes = root.querySelectorAll(SELECTORS.predictiveSearch);
    }

    nodes.forEach(function (el) {
      if (el.dataset.sdController === 'true') return; // already wired — skip
      el.dataset.sdController = 'true';
      // Keep a handle on the instance so the editor's unload can tear it down.
      el.__sdController = new SearchDiscovery(el);
    });
  }

  function teardown(scope) {
    var root = scope || document;
    var nodes = root.matches && root.matches(SELECTORS.predictiveSearch)
      ? [root]
      : root.querySelectorAll(SELECTORS.predictiveSearch);

    nodes.forEach(function (el) {
      if (el.__sdController && typeof el.__sdController.disconnect === 'function') {
        el.__sdController.disconnect();
      }
      el.__sdController = null;
      delete el.dataset.sdController;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor only: re-wire on re-render, tear down on unload.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
    document.addEventListener('shopify:section:select', function (event) {
      init(event.target);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      teardown(event.target);
    });
  }
})();
